import { isLocalAtlasHost } from '@/helpers/atlas-host';

import { AtlasRouteIds } from './constants';

const CKOrgs = ['dualcool', 'ck_internal_test', 'reliability_enablement'];

const crossnokayeIconName = 'crossnokaye';

/**
 * Removes the environment from the facility name
 * @param facilityName - The name of the facility
 * @returns {string} - The facility name without the environment
 */
export const removeEnvironmentFromFacilityName = (facilityName: string) => {
  return !import.meta.env.PROD
    ? facilityName.replace(/^(office |sandbox |janeway )/i, '')
    : facilityName;
};

/**
 * Retrieves the icon name from the organization name
 * @param orgName - The name of the organization
 * @returns {string} - The icon name
 */
export const getIconNameFromOrgName = (orgName: string): string => {
  let iconName = orgName.toLowerCase().replace(/ /g, '_');
  if (CKOrgs.includes(iconName)) {
    iconName = crossnokayeIconName;
  }
  return iconName;
};

/**
 * Builds the Darkwing facility path segment (no host), e.g. `f/org-facility`.
 */
export function buildDarkwingFacilityRelativePath(orgName: string, facilityName: string): string {
  return `f/${orgName}-${facilityName}`;
}

/**
 * Builds the Darkwing path segment (no host), e.g. `f/org-facility/alarms`.
 */
export function buildDarkwingRelativePath(
  orgName: string,
  facilityName: string,
  routeId: string,
  searchParams?: Record<string, string>
): string {
  const facilityPath = buildDarkwingFacilityRelativePath(orgName, facilityName);
  const basePath = `${facilityPath}/${routeId}`;
  const queryString = searchParams ? new URLSearchParams(searchParams).toString() : '';

  return queryString ? `${basePath}?${queryString}` : basePath;
}

/**
 * Builds a complete URL for navigating to Darkwing
 * @param orgName - Organization name
 * @param facilityName - Facility name
 * @param routeId - The route/feature path (e.g., 'alarms', 'equipment')
 * @returns Full URL to Darkwing
 */
export function buildDarkwingUrl(
  orgName: string,
  facilityName: string,
  routeId: string,
  searchParams?: Record<string, string>
): string {
  const isLocal = isLocalAtlasHost(window.location.hostname);
  const darkwingUrl = buildDarkwingRelativePath(orgName, facilityName, routeId, searchParams);

  if (isLocal) {
    return `https://atlaslocal.io:8443/${darkwingUrl}`;
  }

  // Production environments - remove 'go.' prefix
  const darkwingHost = window.location.hostname.replace('go.', '');
  return `${window.location.protocol}//${darkwingHost}/${darkwingUrl}`;
}

/**
 * Builds a complete URL for navigating to a Darkwing panel via deep linking
 * @param orgName - Organization name
 * @param facilityName - Facility name
 * @param panelId - The panel ID (e.g., 'shutdown', 'sensor-calibration')
 * @returns Full URL to Darkwing with panel query parameter
 */
export function buildDarkwingPanelUrl(
  orgName: string,
  facilityName: string,
  panelId: string,
  searchParams?: Record<string, string>
): string {
  const isLocal = isLocalAtlasHost(window.location.hostname);
  const facilitySlug = `${orgName}-${facilityName}`;
  const baseRoute = AtlasRouteIds.IOStatus; // Base route is the last page standing in DW

  const darkwingUrl = `f/${facilitySlug}/${baseRoute}?panel=${panelId}${
    searchParams
      ? `&${Object.entries(searchParams)
          .map(([key, value]) => `${key}=${value}`)
          .join('&')}`
      : ''
  }`;

  if (isLocal) {
    return `https://atlaslocal.io:8443/${darkwingUrl}`;
  }

  // Production environments - remove 'go.' prefix
  const darkwingHost = window.location.hostname.replace('go.', '');
  return `${window.location.protocol}//${darkwingHost}/${darkwingUrl}`;
}
